import { useState } from 'react';
import { Button, Dropdown, FormCheck, FormControl, Modal, Spinner } from 'react-bootstrap';
import { useText } from '../context/TextContext';
import { toast } from 'react-toastify';
import { AdvancedButton } from './AdvancedButton';
import { useUser } from '../context/UserContext';
import { DataTable } from './DataTable';
import { useOrganization } from '../context/OrganizationContext';
import { useForm } from 'react-hook-form';
import { validateEmail } from '../utils/validations';
import { ThreeDotsMenu } from './ThreeDotsMenu';
import { AdvancedDropdownItem } from './AdvancedDropdownItem';
import { createUserInvitationLink } from '../utils/shared/links';
import { createUserInvitation, deleteUserInvitation, setMemberActive, updateUserRole } from '../utils/api';
import { formatDate } from '../utils/date';
import { UserRole } from '../models/shared/UserInvitation';
import InfoTooltip from './InfoTooltip';
import Gravatar from './Gravatar';

interface Props {
    onMissingOrganizationName: () => void;
}

interface Input {
    emails: string;
    role: UserRole;
}

export default function TeamManagement({ onMissingOrganizationName }: Props) {
    const text = useText();
    const { getToken } = useUser();
    const [showDialog, setShowDialog] = useState(false);
    const { organization, orgId, members } = useOrganization();
    const { register, handleSubmit, reset, formState: { errors } } = useForm<Input>();
    const [processing, setProcessing] = useState(false);
    const [roleChangeInvitationId, setRoleChangeInvitationId] = useState<string | undefined>();
    const [newRole, setNewRole] = useState<UserRole>(UserRole.MEMBER);
    const [changingRole, setChangingRole] = useState(false);
    const [togglingActiveId, setTogglingActiveId] = useState<string | undefined>();

    const roleLabel = (role: UserRole): string => {
        if (role === UserRole.ADMIN) return text.admin;
        if (role === UserRole.WORKER) return text.worker;
        return text.manager;
    };

    const handleAddMembers = async (data: Input) => {
        const emails = data.emails.split(',')
            .map(email => email.trim())
            .filter(validateEmail);
        if (emails.length === 0) {
            toast.warning(text.invalidEmails);
            return;
        }

        setProcessing(true);
        try {
            const userToken = await getToken();
            for (const email of emails) {
                await createUserInvitation(userToken, email, orgId, data.role);
            }

            reset();
            setShowDialog(false);
            toast.success(text.invitationsCreated);
        } catch (error) {
            console.error(error);
            toast.error(text.defaultError);
        }
        setProcessing(false);
    };

    const handleAddMemberClick = () => {
        if (!organization.name) {
            onMissingOrganizationName();
            return;
        }
        setShowDialog(true);
    };

    const handleCopyInvitationLink = async (invitationId?: string) => {
        if (!invitationId) {
            return;
        }

        const domain = window.location.origin
        const url = createUserInvitationLink(domain, orgId, invitationId);
        await navigator.clipboard.writeText(url);
        toast.success(text.invitationLinkCopied);
    }

    const handleDeleteUser = async (invitationId?: string) => {
        if (!invitationId) {
            return;
        }

        const userToken = await getToken();
        try {
            await deleteUserInvitation(userToken, orgId, invitationId);
            toast.success(text.defaultSuccess);
        } catch (error) {
            console.error(error);
            toast.error(text.defaultError);
        }
    }

    const handleChangeRoleClick = (invitationId?: string, currentRole?: UserRole) => {
        if (!invitationId) {
            return;
        }

        setRoleChangeInvitationId(invitationId);
        setNewRole(currentRole === UserRole.WORKER ? UserRole.WORKER : UserRole.MEMBER);
    }

    const handleChangeRole = async () => {
        if (!roleChangeInvitationId) {
            return;
        }

        setChangingRole(true);
        try {
            const userToken = await getToken();
            await updateUserRole(userToken, orgId, roleChangeInvitationId, newRole);
            setRoleChangeInvitationId(undefined);
            toast.success(text.roleUpdated);
        } catch (error) {
            console.error(error);
            toast.error(text.defaultError);
        }
        setChangingRole(false);
    }

    const handleToggleActive = async (invitationId?: string, active?: boolean) => {
        if (!invitationId) {
            return;
        }

        setTogglingActiveId(invitationId);
        try {
            const userToken = await getToken();
            await setMemberActive(userToken, orgId, invitationId, !!active);
            toast.success(active ? text.memberReactivated : text.memberPaused);
        } catch (error) {
            console.error(error);
            toast.error(text.defaultError);
        }
        setTogglingActiveId(undefined);
    }

    return (
        <div className='w-100'>
            <div className='d-flex justify-content-between align-items-start'>
                <div>
                    <label className='m-0'>{text.team}</label>
                    <p className='m-0 text-muted small'>{text.teamDescription}</p>
                </div>
                <AdvancedButton variant='secondary' icon='bi-plus' onClick={handleAddMemberClick}>
                    {text.addMember}
                </AdvancedButton>
            </div>
            <DataTable
                data={members}
                columns={[
                    {
                        label: text.user, render: (teamMember) => {
                            return <div className='d-flex align-items-center gap-2' key={teamMember.id}>
                                <Gravatar identifier={teamMember.email} size='xs' default='wavatar' />
                                <div>{teamMember.email}</div>
                            </div>
                        }
                    },
                    { label: text.role, render: (teamMember) => roleLabel(teamMember.role) },
                    {
                        label: text.status, render: (teamMember) => {
                            if (teamMember.role === 'admin') {
                                return text.admin
                            }

                            if (teamMember.status === 'paused') {
                                return <span className='text-muted'><i className='bi bi-pause-circle me-2' />{text.paused}</span>
                            }

                            if (teamMember.status === 'accepted') {
                                return <span className='text-success'>{text.accepted} ({formatDate(teamMember.acceptedAt)})</span>
                            }

                            if (teamMember.status === 'new') {
                                return <span className='text-info'><Spinner size='sm' className='me-2' />{text.sendingInvitation}</span>
                            }

                            return <span className='text-info'>{text.invitationSent}</span>
                        }
                    },
                    {
                        label: text.actions,
                        align: 'right',
                        render: (userInvitation) => {
                            if (userInvitation.role === 'admin') {
                                return <></>
                            }

                            const isPaused = userInvitation.status === 'paused';
                            const isToggling = togglingActiveId === userInvitation.id;

                            return <ThreeDotsMenu>
                                <AdvancedDropdownItem icon='bi-copy' text={text.copyInvitationLink} onClick={() => handleCopyInvitationLink(userInvitation.id)} />
                                <AdvancedDropdownItem icon='bi-person-gear' text={text.changeRole} onClick={() => handleChangeRoleClick(userInvitation.id, userInvitation.role)} />
                                {isPaused
                                    ? <AdvancedDropdownItem icon='bi-play-circle' text={text.reactivateMember} disabled={isToggling} onClick={() => handleToggleActive(userInvitation.id, true)} />
                                    : <AdvancedDropdownItem icon='bi-pause-circle' text={text.pauseMember} disabled={isToggling} onClick={() => handleToggleActive(userInvitation.id, false)} />}
                                <Dropdown.Divider />
                                <AdvancedDropdownItem icon='bi-trash' text={text.delete} onClick={() => handleDeleteUser(userInvitation.id)} />
                            </ThreeDotsMenu>

                        }
                    }
                ]}
            />

            <Modal show={showDialog} onHide={() => setShowDialog(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{text.addMember}</Modal.Title>
                </Modal.Header>
                <form
                    onSubmit={(e) => {
                        e.stopPropagation();
                        handleSubmit(handleAddMembers)(e);
                    }}
                >
                    <Modal.Body>
                        <div className="d-flex flex-column gap-3">
                            <div className="d-flex flex-column gap-2">
                                <label>{text.enterEmails}</label>
                                <FormControl
                                    as="textarea"
                                    className={`form-control ${errors.emails ? 'is-invalid' : ''}`}
                                    {...register('emails', {
                                        required: text.invalidEmails,
                                        validate: (value) => {
                                            const emails = value.split(',').map(email => email.trim());
                                            return emails.every(validateEmail) || text.invalidEmails;
                                        }
                                    })}
                                />
                                {errors.emails && (
                                    <div className="invalid-feedback">
                                        {errors.emails.message}
                                    </div>
                                )}
                            </div>

                            <div className="d-flex flex-column gap-2">
                                <label>{text.role}</label>
                                <div className="d-flex gap-3">
                                    <FormCheck
                                        type="radio"
                                        id="role-member"
                                        value={UserRole.MEMBER}
                                        {...register('role', { required: true })}
                                        defaultChecked
                                        label={<InfoTooltip content={text.memberDescription} placement="top" label={text.manager} />}
                                    />
                                    <FormCheck
                                        type="radio"
                                        id="role-worker"
                                        value={UserRole.WORKER}
                                        {...register('role', { required: true })}
                                        label={<InfoTooltip content={text.workerDescription} placement="top" label={text.worker} />}
                                    />
                                </div>
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowDialog(false)}>
                            {text.cancel}
                        </Button>
                        <AdvancedButton variant="primary" type="submit" processing={processing}>
                            {text.add}
                        </AdvancedButton>
                    </Modal.Footer>
                </form>
            </Modal>

            <Modal show={!!roleChangeInvitationId} onHide={() => setRoleChangeInvitationId(undefined)}>
                <Modal.Header closeButton>
                    <Modal.Title>{text.changeRoleModalTitle}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex flex-column gap-2">
                        <label>{text.role}</label>
                        <div className="d-flex gap-3">
                            <FormCheck
                                type="radio"
                                id="change-role-member"
                                checked={newRole === UserRole.MEMBER}
                                onChange={() => setNewRole(UserRole.MEMBER)}
                                label={<InfoTooltip content={text.memberDescription} placement="top" label={text.manager} />}
                            />
                            <FormCheck
                                type="radio"
                                id="change-role-worker"
                                checked={newRole === UserRole.WORKER}
                                onChange={() => setNewRole(UserRole.WORKER)}
                                label={<InfoTooltip content={text.workerDescription} placement="top" label={text.worker} />}
                            />
                        </div>
                    </div>
                    <p className="text-muted small mt-3 mb-0">{text.changeRoleDelayWarning}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setRoleChangeInvitationId(undefined)}>
                        {text.cancel}
                    </Button>
                    <AdvancedButton variant="primary" processing={changingRole} onClick={handleChangeRole}>
                        {text.save}
                    </AdvancedButton>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
